// TypeScript mirrors of internal/leaderboard/types.go DTOs and the package-
// level constants in internal/leaderboard/leaderboard.go. Enum literals are
// the source-of-truth strings enforced by Postgres CHECK constraints in
// migrations 0021, 0022, 0024, 0025, 0029, 0030, 0031, 0032, 0033.

/** Score operator. CHECK lb_operator_check (migration 0021). */
export type Operator = "best" | "worst" | "increment" | "set";

/** Sort direction for a leaderboard. CHECK lb_sort_check (migration 0021). */
export type Sort = "desc" | "asc";

/**
 * Time window. CHECK lb_window_check (migrations 0021 + 0025 + 0075).
 *
 * "recurring" and "recurring_event" both cut their cycles by a cron, and the
 * same column means two different things in them: on "recurring" a tick CUTS a
 * cycle, so the successor opens where the previous one closed and the board is
 * never shut; on "recurring_event" a tick STARTS a window that ends
 * `window_minutes` later, and what is left of the interval is a pause during
 * which the board takes no submits.
 */
export type WindowKind = "all_time" | "recurring" | "event" | "recurring_event";

/**
 * Aggregation scope. CHECK lb_scope_check (migrations 0021 + 0031 + 0032).
 * "guild_aggregated" requires guild_aggregator + operator pairing.
 */
export type Scope = "global" | "guild_internal" | "guild_aggregated";

/**
 * Per-guild aggregator. CHECK lb_guild_aggregator_check (migration 0032).
 * Non-null only when scope=guild_aggregated. Pairing: sum↔increment, max↔best.
 */
export type GuildAggregator = "sum" | "max";

/**
 * Who computes a guild_aggregated board's score. CHECK
 * lb_guild_score_source_check (migration 0060).
 *
 * "submit" is every board's behaviour before this column existed, and stays
 * the default: members send scores and each submit is folded into their
 * guild's. "player_stat" hands the arithmetic to the server, which aggregates
 * `guild_stat_key` over the CURRENT members' `players.stats` — so the score is
 * a snapshot rather than a history, `POST /submit` answers 403
 * `leaderboard_auto_scored`, and the board is confined to window_kind=all_time
 * (there is nothing a cycle reset could take away).
 *
 * "member_contribution" is the team-battle shape: the guild's score is the sum
 * of what its PARTICIPATING members contributed to this cycle, so the server
 * fixes guild_aggregator="sum" and operator="increment" itself, refuses
 * window_kind="all_time" (in an endless cycle "was in the team when it
 * entered" degenerates) and opens three fields of its own —
 * `min_guild_members`, `reward_contributors_only` and
 * `late_joiners_may_score`.
 *
 * Fixed at create: UpdateParams (internal/leaderboard/types.go) carries no
 * field for it, and the admin decoder's DisallowUnknownFields refuses a PATCH
 * that names one — which is why UpdateLeaderboardInput below has none either.
 */
export type GuildScoreSource = "submit" | "player_stat" | "member_contribution";

/** Read-side visibility. CHECK lb_visibility_check (migrations 0021 + 0031). */
export type Visibility = "public" | "guild_only" | "hidden";

/**
 * Score segmentation. CHECK lb_segmentation_check (migrations 0025 + 0085).
 *
 * The first three PARTITION one ranking and their membership is symmetric — if
 * you are in my room, I am in yours. "personal" is the odd one out: every player
 * gets their OWN list of opponents, assembled on their first contact with the
 * board and frozen for the cycle, and the lists are asymmetric — a player can be
 * on your list while you are not on theirs.
 */
export type Segmentation = "none" | "bucket" | "tier" | "personal";

/**
 * One selection rule of a personal board's matchmaking. Mirrors
 * leaderboard.MatchmakingLayer.
 *
 * Layers are consumed top to bottom: each takes no more than its quota and the
 * remainder goes to the next one. The vocabulary is CLOSED — every kind is a
 * query with a bounded window and an index behind it — and the LAST layer must
 * be a filler ("recent_active" or "bots"), or a list can stay incomplete, which
 * is the one thing this board type exists to prevent.
 */
export type MatchmakingLayerKind =
  | "friends"
  | "guild"
  | "similar_pace"
  | "recent_active"
  | "same_country"
  | "bots";

/** How a layer orders the candidates it found, once it has more than its quota. */
export type MatchmakingPrefer = "same_timezone" | "fresher_last_seen" | "random";

export interface MatchmakingLayer {
  kind: MatchmakingLayerKind;
  /** Absent means «the rest» — everything still missing from the list. */
  quota?: number | null;
  prefer?: MatchmakingPrefer;
  /** similar_pace only: ±% around the owner's strength. */
  band_pct?: number | null;
  /** similar_pace only: how many times the band may double when it comes up
     short. 0 (or absent) never widens. */
  widen_steps?: number | null;
}

/**
 * The threshold a player must clear to be in the cycle at all: a key of
 * `players.stats` and its minimum. Below it no list is assembled for them and
 * they appear on nobody else's.
 */
export interface EntryGate {
  key: string;
  min: number;
}

/**
 * What counts as an opponent of the player's own level. `primary` is always
 * "previous_cycle_score" — the pace of a week, not a year of progress — and
 * `fallback_stat_key` is what a player with no previous cycle is measured by.
 * Absent means «treat them as a newcomer»: the pace layer yields nothing for
 * them and the remainder goes down.
 */
export interface StrengthMetric {
  primary: "previous_cycle_score";
  fallback_stat_key?: string | null;
}

/** Parsed shape of the leaderboards.matchmaking_config JSONB column. Mirrors
   leaderboard.MatchmakingConfig. Non-null exactly on segmentation="personal"
   (CHECK lb_personal_check, migration 0085). */
export interface MatchmakingConfig {
  entry_gate?: EntryGate | null;
  /** How recently a candidate must have been seen (`players.last_seen_at`) to
     be worth putting on somebody's list. */
  freshness_days: number;
  strength: StrengthMetric;
  layers: MatchmakingLayer[];
}

/**
 * Tier mode (required when segmentation='tier').
 * CHECK lb_tier_check (migrations 0025 + 0044, which widened the constraint to
 * the third mode: 'claim_gated' — the tier moves when the player claims the
 * threshold reward, never on score alone).
 */
export type TierMode = "score_bucket" | "promotion" | "claim_gated";

/** Submit policy. CHECK lb_submit_policy_check (migration 0021). */
export type SubmitPolicy = "client" | "s2s_only";

/** Leaderboard lifecycle. CHECK lb_status_check (migration 0021). */
export type LeaderboardStatus = "active" | "paused" | "archived";

/** Cycle lifecycle. CHECK lbc_status_check (migration 0022). */
export type CycleStatus = "scheduled" | "open" | "closing" | "closed" | "archived";

/** Submit audit channel. CHECK lba_channel_check (migrations 0024 + 0033). */
export type AuditChannel = "client" | "s2s" | "admin";

/** Entity kind for scores. CHECK lbs_entity_kind_check (migration 0032). */
export type EntityKind = "player" | "guild";

/** Reward grant lifecycle. CHECK lb_grants_status_check (migration 0030). */
export type GrantStatus = "pending" | "claimed" | "expired";

/** Anti-cheat caps. Mirrors leaderboard.Guards. */
export interface Guards {
  max_score_per_submit?: number | null;
  max_delta_per_submit?: number | null;
  min_interval_sec?: number | null;
}

/** One tier entry inside TierConfig.tiers. Mirrors leaderboard.Tier. */
export interface Tier {
  key: string;
  name: string;
  min_score?: number | null;
}

/** Parsed shape of the leaderboards.tier_config JSONB column. Mirrors leaderboard.TierConfig. */
export interface TierConfig {
  tiers: Tier[];
  promote_top_k?: number | null;
  demote_bottom_k?: number | null;
}

/** Runtime view of a leaderboard definition row. Mirrors leaderboard.Leaderboard. */
export interface Leaderboard {
  id: string;
  ext_key: string;
  name: string;
  description: string;
  scope: Scope;
  /** Populated only when scope=guild_aggregated (Phase 5.3b). */
  guild_aggregator?: GuildAggregator;
  /**
   * Always present — the Go field carries no `omitempty`, and the column is
   * NOT NULL DEFAULT 'submit', so every board answers with one of the two
   * values whether or not it was created after t-100.
   */
  guild_score_source: GuildScoreSource;
  /**
   * The `players.stats` key a guild_score_source="player_stat" board is scored
   * from — an `int` field of the project's `profile.stats_schema`. Non-null
   * exactly when the source is "player_stat" (CHECK lb_guild_stat_key_check),
   * so its absence is what says the board is scored by submits.
   */
  guild_stat_key?: string;
  /**
   * guild_score_source="member_contribution" only: how many members a team
   * needs before it may enter the battle at all. Checked only while the team
   * has NOT entered yet — a submit from a team below the threshold answers
   * `applied=false, reason="team_too_small"` and takes no room seat; once the
   * team is in, dropping below the threshold changes nothing.
   *
   * `null` or `0` both mean "no threshold". Patchable, unlike the source it
   * belongs to.
   */
  min_guild_members?: number | null;
  /**
   * guild_score_source="member_contribution" only, default `true`: the team's
   * placement grant goes to its CONTRIBUTORS rather than to every current
   * member. Off restores the old fan-out. It says nothing about the
   * contributor rewards, where a zero contribution is excluded by the nature
   * of the ranking. Patchable.
   */
  reward_contributors_only?: boolean;
  /**
   * guild_score_source="member_contribution" only, default `true`: taking part
   * in the battle is "is in the guild right now". `false` is the strict rule —
   * "was in the guild when the team entered, and is in it now" — under which a
   * latecomer's submit answers `applied=false, reason="not_participating"`.
   * Boards cut before the column existed carry `false`; new ones open on
   * `true`. Patchable, and forward only: a refused submit is stored nowhere,
   * so opening the rule banks nothing retroactively.
   */
  late_joiners_may_score?: boolean;
  operator: Operator;
  sort: Sort;
  window_kind: WindowKind;
  cron?: string;
  event_start_at?: string;
  event_end_at?: string;
  /**
   * How long a window stays open once the cron starts it. Present on
   * window_kind=recurring_event and on nothing else — the column is NULL
   * everywhere else (CHECK lb_window_fields_check, migration 0075) and the Go
   * field carries `omitempty`, so its ABSENCE is what says the board's cycles
   * run back to back.
   *
   * Minutes, because that is what the column holds and what the API carries;
   * only the config form reads it in a larger unit where one divides it
   * exactly, and it prints the total beside the row so the operator reads
   * exactly what will be sent.
   */
  window_minutes?: number;
  segmentation: Segmentation;
  bucket_size?: number;
  /**
   * How many players are on one personal list, the owner included. Non-null
   * exactly on segmentation="personal" (CHECK lb_personal_check, migration
   * 0085) — unlike bucket_size it is not the size of anything shared: nothing
   * is divided into lists of ten, every player simply gets ten rows of their
   * own.
   *
   * Patchable, and a new value reaches the lists at the NEXT cycle: the open
   * one holds its length in its own stamp, so one week can never hold lists of
   * two different sizes.
   */
  roster_size?: number;
  /** The rules those rows are picked by. Present on a personal board and on no
     other. Patchable. */
  matchmaking_config?: MatchmakingConfig;
  tier_mode?: TierMode;
  tier_config?: TierConfig;
  realtime: boolean;
  submit_policy: SubmitPolicy;
  guards: Guards;
  visibility: Visibility;
  /** Free-form JSON. Backend uses json.RawMessage. */
  metadata?: unknown;
  status: LeaderboardStatus;
  /**
   * Keeps a player in the cycle they just finished until they close it
   * themselves — by claiming its reward or by looking at the results. While
   * they have not, reads answer for THAT cycle and submits are refused with
   * `leaderboard_cycle_pending`. CHECK lb_hold_finished_check (migration 0049,
   * widened by 0075) allows it only on the window kinds where a cycle FINISHES
   * — recurring|event|recurring_event — and never on scope=guild_aggregated.
   */
  hold_finished_cycles: boolean;
  /**
   * When the toggle above was last switched on. Only cycles that finish after
   * it hold anybody, which is what keeps a board with a history from queueing
   * up one results screen per cycle the day the toggle goes on. Read-only:
   * the server stamps it in the same write that turns the toggle on.
   */
  hold_since?: string;
  /**
   * How long a finished cycle is kept — and, by the same number, how long the
   * rewards it handed out can be claimed. CHECK lb_retention_days_check
   * (migration 0049): >= 0, where 0 means «keep nothing», the rows going as
   * soon as the rewards are counted.
   */
  retention_days: number;
  /**
   * A second VIEW of the same cycle: the rows filtered down to the player's
   * own country and ranked 1..N among them. Not a second competition — the
   * cycle, the scores, the participants count and the rewards all stay the
   * world's, and only the ordering the client asks for changes.
   *
   * CHECK lb_country_split_check (migration 0056) allows it on scope
   * global|guild_aggregated with segmentation='none' alone: guild_internal
   * already ranks inside one guild, and a bucket or tier board already ranks
   * inside a partition. Editable on a live board, unlike segmentation.
   */
  country_split: boolean;
  /**
   * Puts a player on the board with score 0 the first time they READ it,
   * instead of only when they first submit. The row is an ordinary row from
   * then on: it ranks, it counts towards participants, and it takes rewards
   * like any other — which is why a percentile band on such a board is
   * measured against everyone who opened it rather than everyone who played
   * it.
   *
   * CHECK lb_auto_enroll_check (migration 0074) refuses it on sort='asc',
   * where a 0 would be first place, and on scope='guild_aggregated', where the
   * row belongs to a guild whose score the server composes. Editable on a live
   * board; `sort` is judged together with it on every PATCH.
   */
  auto_enroll: boolean;
  created_at: string;
  updated_at: string;
}

/** Admin list filter — passed as `status` and `window_kind` query params. */
export interface ListFilter {
  status?: LeaderboardStatus | "";
  window_kind?: WindowKind | "";
}

/** POST /leaderboards body. Mirrors leaderboard.CreateParams. */
export interface CreateLeaderboardInput {
  ext_key: string;
  name: string;
  description: string;
  /** "" defaults to "global" (Phase 5.3a). */
  scope?: Scope | "";
  /** Required when scope=guild_aggregated (Phase 5.3b) — except on
     guild_score_source="member_contribution", where the server fixes it to
     "sum" itself and the body leaves it out. */
  guild_aggregator?: GuildAggregator | "";
  /** Omitted defaults to "submit". "player_stat" additionally requires
     scope=guild_aggregated, window_kind=all_time and a guild_stat_key.
     "member_contribution" requires scope=guild_aggregated and a window_kind
     that is not all_time, and brings its own two fields below. */
  guild_score_source?: GuildScoreSource;
  /** Required exactly when guild_score_source="player_stat", forbidden
     otherwise (validateGuildScoreSourceCreate). Sent byte for byte: the
     server refuses a key that differs from its own trimmed form rather than
     trimming it, because `players.stats` keys are matched exactly. */
  guild_stat_key?: string;
  /** guild_score_source="member_contribution" only — the server answers 400
     for it on any other source. `null` or `0` means "no threshold". */
  min_guild_members?: number | null;
  /** guild_score_source="member_contribution" only, likewise. Omitted defaults
     to `true` server-side; the form sends what its switch is showing. */
  reward_contributors_only?: boolean;
  /** guild_score_source="member_contribution" only, and defaulted the same
     way: a board created here lets anyone currently in the guild score. */
  late_joiners_may_score?: boolean;
  /**
   * Optional for the two auto-paired board shapes: on
   * guild_score_source="player_stat" the operator is INERT — the server
   * derives it from guild_aggregator (sum↔increment, max↔best) and refuses a
   * create body that names one at all (R3, spec §4.1) — and on
   * "member_contribution" the server fixes the pair itself
   * (increment ↔ sum). Everywhere else it is required, and the form always
   * sends it.
   */
  operator?: Operator;
  sort: Sort;
  window_kind: WindowKind;
  cron?: string;
  event_start_at?: string;
  event_end_at?: string;
  /** Required exactly on window_kind=recurring_event, refused everywhere else
     (validateCreate) — the length of the window its cron starts. Whole
     minutes, >= 1, and strictly shorter than the gap between two starts: the
     leftover IS the pause, and a length that reaches the next start would put
     two open cycles on one board. The server measures that gap with the cron
     parser the cycle worker uses and answers 400 when it does not fit. */
  window_minutes?: number;
  segmentation?: Segmentation | "";
  /** Room size. On segmentation=bucket it IS the segmentation; on
     segmentation=tier it splits every league into rooms, and null means the
     league competes as one list. */
  bucket_size?: number | null;
  /** Required exactly on segmentation="personal", refused everywhere else
     (validatePersonal): how long one personal list is, the owner included. */
  roster_size?: number;
  /** Required exactly on segmentation="personal", refused everywhere else. The
     last layer must be a filler — the server answers 400
     leaderboard_invalid_segmentation otherwise. */
  matchmaking_config?: MatchmakingConfig;
  tier_mode?: TierMode;
  tier_config?: TierConfig;
  realtime?: boolean;
  /**
   * Optional for the same board shape `operator` is, and for the same reason:
   * on guild_score_source="player_stat" nothing submits, so who may submit is
   * not a setting the form shows — and an omitted value is not a gap, it is
   * the column's own default ("client", service.CreateLeaderboard's
   * `valOr(p.SubmitPolicy, SubmitPolicyClient)`).
   */
  submit_policy?: SubmitPolicy;
  /**
   * Likewise: the caps are rules about an incoming submit, and an auto-scored
   * board takes none. Omitted leaves every cap unset on create and the stored
   * ones untouched on a PATCH — which is why it is an absence here and not an
   * empty object.
   */
  guards?: Guards;
  visibility: Visibility;
  metadata?: unknown;
  /** Omitted defaults to false. Rejected together with retention_days = 0 —
     a cycle deleted at close holds nobody. */
  hold_finished_cycles?: boolean;
  /** Omitted defaults to DefaultRetentionDays (90). */
  retention_days?: number;
  /** Omitted defaults to false. Only scope global|guild_aggregated with
     segmentation=none takes it — CHECK lb_country_split_check (migration
     0056), which the create validator answers with a 400 before the DB does. */
  country_split?: boolean;
  /** Omitted defaults to false. Refused on sort=asc and on
     scope=guild_aggregated — CHECK lb_auto_enroll_check (migration 0074),
     which the create validator answers with a 400 before the DB does. */
  auto_enroll?: boolean;
}

/**
 * PATCH /leaderboards/{lb_id} body. Mirrors leaderboard.UpdateParams.
 * All pointer fields in Go are optional here; `null` is sent to clear nullable
 * columns (e.g. cron, event_*).
 *
 * The fields NOT here are the point of the type: window_kind, scope,
 * segmentation and tier_mode are decided at create and never decoded by the
 * PATCH handler — the cycles already cut, and the rows already written, belong
 * to the values the board was created with.
 */
export interface UpdateLeaderboardInput {
  name?: string;
  description?: string;
  /** Only on an ARCHIVED board (leaderboard_immutable_field otherwise): the key
     is the handle shipped game builds submit to, and archiving is what stops
     them being answered at all. Renaming an archived board is what frees the
     key — UNIQUE (game_id, ext_key) counts archived rows too. */
  ext_key?: string;
  /** How an incoming score is read. Applies from the next submit on; scores
     already stored are not recomputed. The server checks the pairing rules
     (guild_aggregator sum↔increment / max↔best, and increment is refused on a
     score_bucket tier board) against the columns the board is stored with. */
  operator?: Operator;
  /** Score sort direction, independent of `operator`. Changing it re-draws the
     ranks of the cycle running right now: the server drops that cycle's Redis
     partitions and the reader rebuilds them in the new order. */
  sort?: Sort;
  /** scope=guild_aggregated only — the scope itself is immutable, so this is
     patchable exactly where the column is non-null to begin with.

     On an AUTO-SCORED board naming this field rescores every guild of the
     project, unconditionally — the server does not compare it to the stored
     value first, because a diff gate would make a rescore that failed
     unrepeatable. So it travels only when it actually moved; see
     SettingsTab.buildUpdatePayload. */
  guild_aggregator?: GuildAggregator;
  /** guild_score_source="player_stat" boards only — anywhere else the server
     answers leaderboard_immutable_field. Repointing the board at another key
     rescores every guild of the project, and carries the same
     "only when it moved" rule as guild_aggregator above, for the same
     reason. There is deliberately no guild_score_source here: the source is
     fixed at create, and the admin decoder rejects a body that names it. */
  guild_stat_key?: string;
  /** guild_score_source="member_contribution" boards only. Absent leaves the
     threshold as it is; `null` clears it, which is the same as `0` — no
     threshold. It is re-read only while a team has not entered yet, so moving
     it mid-cycle never evicts a team that is already in. */
  min_guild_members?: number | null;
  /** guild_score_source="member_contribution" boards only. Who the team's
     placement grant fans out to at close — its contributors (true) or every
     current member (false). */
  reward_contributors_only?: boolean;
  /** guild_score_source="member_contribution" boards only. Who counts as
     taking part — anyone in the guild now (true), or only the members who were
     in it when the team entered (false). Unlike the flag above it, this one
     has been deciding sums since the cycle opened, so naming it drags a
     restatement of the open cycle behind the patch. */
  late_joiners_may_score?: boolean;
  cron?: string | null;
  /** recurring_event only — anywhere else the server answers
     leaderboard_immutable_field. It is the other half of the same schedule
     `cron` describes, and the overlap rule is re-judged on the PAIR the board
     will have after the patch, so a length that fits today's cron can be
     refused under a new one arriving in the same body. */
  window_minutes?: number;
  /** When a changed schedule starts cutting cycles. Meaningless without
     something to apply, so it never travels alone: "next_cycle" leaves the
     open cycle closing on the date players were told, "current_cycle"
     additionally moves its close_at — to the first tick of the new schedule
     after now on a `recurring` board, and to `open_at` plus the new length on
     a `recurring_event` one, whose start never moves.

     On `recurring` its companion is `cron`. On `recurring_event` either half
     is one: the length re-cuts the window exactly as the cron does, so a body
     carrying `window_minutes` alone may carry this too. */
  cron_apply?: "next_cycle" | "current_cycle";
  event_start_at?: string | null;
  event_end_at?: string | null;
  /** segmentation=tier only — the rooms are the one part of segmentation that
     moves after create. Omitted leaves them as they are, `null` switches them
     off, a number resizes them. Sending it for a bucket board is a 400
     (leaderboard_immutable_field): there the size IS the segmentation. */
  bucket_size?: number | null;
  /**
   * The ONE part of a board's shape that moves after create, and in exactly one
   * direction: "bucket" → "personal". It is patchable at all only because a
   * cycle carries its own stamp of the mode its rows are read by, so the change
   * lands on the NEXT cycle and the open one plays out on its rooms. Anything
   * else is leaderboard_immutable_field.
   *
   * It never travels alone: a bucket board turned personal has no list length
   * and no rules to assemble by, so `roster_size` and `matchmaking_config` come
   * with it in the same body. The server clears `bucket_size` itself in the
   * same statement — the two cannot stand side by side.
   */
  segmentation?: Segmentation;
  /** segmentation="personal" boards only. A new value reaches the lists at the
     next cycle: the open one holds its length in its stamp. */
  roster_size?: number;
  /** segmentation="personal" boards only. Frozen lists are never rebuilt, so a
     change reaches the lists assembled from now on. */
  matchmaking_config?: MatchmakingConfig;
  tier_config?: TierConfig | null;
  realtime?: boolean;
  submit_policy?: SubmitPolicy;
  guards?: Guards;
  visibility?: Visibility;
  metadata?: unknown;
  status?: LeaderboardStatus;
  /** The cycle lifetime pair, both patchable. window_kind and scope are not,
     so the backend checks the toggle against the values the board is stored
     with — there is no combination of a PATCH and a board that can slip past
     migration 0049's CHECKs. */
  hold_finished_cycles?: boolean;
  retention_days?: number;
  /** The one toggle of the country view, and — unlike segmentation, which it
     is gated on — patchable on a live board. Switching it ON also stamps the
     open cycle's rows with their entity's country, so the country tab answers
     with the standings that already exist; switching it off touches no data. */
  country_split?: boolean;
  /** Enrolment on read, patchable on a live board. `sort` is patchable too, so
     the backend judges the pair on the values the board will HAVE — a PATCH
     that turns a board enrolling readers around to asc is refused with
     validation_failed, whichever half of the pair it carries. Switching it on
     writes nobody by itself; switching it off leaves every row already
     written, they are ordinary rows. */
  auto_enroll?: boolean;
}

/**
 * What a retention_days of `days` would delete on one board at the next sweep.
 * Mirrors leaderboard.RetentionImpact. The config form asks for it before
 * saving a SHORTER term: `unclaimed_grants` is the part of that decision which
 * cannot be undone.
 */
export interface RetentionImpact {
  days: number;
  cycles: number;
  unclaimed_grants: number;
}

/**
 * What DELETE would take with the board. Mirrors leaderboard.DeleteImpact.
 * Asked while the confirmation dialog is open, because the numbers are the
 * only part of that decision the operator cannot see from the page they are
 * on — and unlike RetentionImpact, none of this comes back at all.
 *
 * `rewards` counts definitions, not payouts: it is the smallest number here
 * and the only one that is merely configuration.
 */
export interface DeleteImpact {
  cycles: number;
  score_rows: number;
  unclaimed_grants: number;
  rewards: number;
}

/**
 * The scope line a guild_score_source="player_stat" board shows before it
 * goes live: how many of the project's players carry `key` in their stats,
 * out of how many players the project has ("21,904 of 24,110 players carry
 * `level`"). Mirrors the admin players/stat-key-coverage response — unlike
 * the rest of this file, that DTO lives in internal/admin/api/handler, not
 * internal/leaderboard, because the scope check runs against the players
 * table directly and needs no board to exist yet.
 */
export interface StatKeyCoverage {
  key: string;
  carrying: number;
  total: number;
}

/**
 * One time-window of a leaderboard. Mirrors leaderboard.Cycle.
 *
 * `segmentation`, `roster_size` and `bucket_size` are the CYCLE STAMP: how this
 * window's rows are to be read, recorded when it was cut. They are what makes
 * the mode movable on a live board — and what every screen here must branch on
 * instead of on the board's current mode, or a week that was played in rooms is
 * drawn as something it never was. `cycleShape` in segments.ts is where that
 * substitution is made, so no screen has to remember to make it.
 */
export interface Cycle {
  id: string;
  leaderboard_id: string;
  open_at: string;
  close_at?: string;
  status: CycleStatus;
  closed_at?: string;
  participants: number;
  segmentation: Segmentation;
  /** The length of this cycle's personal lists; absent on every cycle without
     them. */
  roster_size?: number;
  /** The size of this cycle's rooms; absent on a cycle that has none. It is the
     only copy left once a bucket board moves to personal lists, because that
     patch clears the board's own. */
  bucket_size?: number;
  /** The board configuration this cycle was played under, recorded when it was
     cut and again when it closed. Absent on cycles cut before snapshots
     existed, and on those alone the board's current settings are the only
     account of the week there is. It is what lets a closed cycle be explained
     by its own rules rather than by whatever the board became afterwards. */
  config_snapshot?: CycleConfigSnapshot;
  /** The prize table the cycle was paid by. Only on the single-cycle read: a
     whole table per row is not something a list of fifty cycles should carry. */
  rewards_snapshot?: RewardSpec[];
  created_at: string;
}

/** The half of a board's configuration that decides what a cycle's rows MEAN —
    frozen per cycle. The three fields it does not carry (segmentation,
    roster_size, bucket_size) are columns of the cycle itself, above. */
export interface CycleConfigSnapshot {
  scope: Scope;
  operator: Operator;
  sort: Sort;
  window_kind: WindowKind;
  guild_aggregator?: GuildAggregator;
  guild_score_source: GuildScoreSource;
  guild_stat_key?: string;
  tier_mode?: TierMode;
  tier_config?: TierConfig;
  matchmaking_config?: MatchmakingConfig;
  country_split: boolean;
  min_guild_members?: number;
  reward_contributors_only?: boolean;
  late_joiners_may_score?: boolean;
}

// ---------------------------------------------------------------------------
// The personal mode's two admin reads. There are exactly two on purpose: an
// operator has two questions about matchmaking — what did this cycle's lists
// come out of, and show me THIS player's list — and both are answered by a key
// lookup rather than by a page of rosters.
// ---------------------------------------------------------------------------

/** One bar of the composition: a layer and the opponent slots it filled. */
export interface RosterSourceCount {
  source: MatchmakingLayerKind;
  slots: number;
}

/** One bucket of the bot histogram: how many lists hold exactly this many bots. */
export interface RosterBotCount {
  bots: number;
  lists: number;
}

/**
 * What a personal cycle's lists were assembled OUT OF. Mirrors
 * leaderboard.RosterComposition.
 *
 * Counted as the lists are written, never scanned out of them, so a cycle of a
 * million lists answers as cheaply as a cycle of one. `lists` is the number of
 * rosters — the owner's own slot is not a source and never appears in
 * `sources`.
 */
export interface RosterComposition {
  lists: number;
  opponent_slots: number;
  sources: RosterSourceCount[] | null;
  bot_lists: RosterBotCount[] | null;
}

/** One row of a player's list, with the layer that put it there. Mirrors
   leaderboard.RosterEntry; `source` is "self" on the owner's own row. */
export interface RosterEntry extends Entry {
  source: MatchmakingLayerKind | "self";
  /** When this opponent was last on — the very thing the matchmaker selected
     by, so it is what answers «half of these were last on three weeks ago».
     `null` is a state of its own: this player has never held a connection. */
  last_seen_at: string | null;
}

/** One player's list as the inspector shows it. Mirrors leaderboard.RosterView. */
export interface RosterView {
  cycle_id: string;
  owner_id: string;
  assembled_at: string;
  owner_rank: number;
  members: RosterEntry[] | null;
}

/** Resolved player display data on an entry. Mirrors leaderboard.EntryPlayer. */
export interface EntryPlayer {
  id: string;
  display_name: string;
  avatar: string | null;
}

/** Resolved guild data when ?expand=guild. Mirrors leaderboard.EntryGuild. */
export interface EntryGuild {
  id: string;
  name: string;
  tag: string;
  logo: string | null;
  role: string;
}

/** One row of a leaderboard read response. Mirrors leaderboard.Entry. */
export interface Entry {
  rank: number;
  score: number;
  is_me: boolean;
  updated_at: string;
  tier_key?: string;
  bucket_id?: string;
  player: EntryPlayer;
  guild?: EntryGuild;
  payload?: unknown;
}

// ---------------------------------------------------------------------------
// One team's inside of a member_contribution cycle — what the admin
// contributions endpoint answers with. It exists because nothing else on the
// board can say it: /top ranks the GUILDS, and the second reward table is
// ranked inside each of them.
// ---------------------------------------------------------------------------

/** The guild card carried on a contributions response. */
export interface ContributionGuild {
  id: string;
  name: string;
  logo: string | null;
  meta: unknown;
  role: string;
  member_count: number;
  max_members: number;
}

/** Where the team itself stands in the cycle. */
export interface GuildContributionEntry {
  guild: ContributionGuild;
  /** Whether the team took a room seat in this cycle at all. */
  entered: boolean;
  score: number;
  /** All three are absent until the team has entered. */
  rank?: number;
  bucket_id?: string;
  entered_at?: string;
}

/** One member of the team, as the second reward table ranks them. */
export interface GuildContributionMember {
  /**
   * Place by contribution inside this team. ABSENT when the member is not
   * participating, or when their contribution is not above zero — which is the
   * same thing the contributor rewards say: a place nobody qualifies for is
   * simply not awarded.
   */
  rank?: number;
  player: EntryPlayer & { avatar_url?: string | null };
  role: string;
  score: number;
  submit_count: number;
  participating: boolean;
  is_me: boolean;
}

/**
 * GET …/cycles/{cycle_id}/guilds/{guild_id}/contributions. `members` arrives in
 * the order the screen shows it — contributors by descending score, then
 * participants with nothing yet, then non-participants — so nothing re-sorts
 * it here.
 */
export interface GuildContributions {
  guild: GuildContributionEntry;
  members: GuildContributionMember[];
}

/** One row of leaderboard_buckets. Mirrors leaderboard.Bucket. */
export interface Bucket {
  id: string;
  cycle_id: string;
  /** The league this room belongs to; absent on a segmentation=bucket board. */
  tier_key?: string;
  bucket_size: number;
  member_count: number;
  created_at: string;
}

/**
 * Constant-size aggregate over a cycle's buckets. Mirrors
 * leaderboard.BucketSummary — the reason it exists is that `Bucket[]` does not
 * fit the screen it feeds: a segmented board can hold thousands of rooms, and
 * the Overview only ever needed these three numbers.
 */
export interface BucketSummary {
  count: number;
  members: number;
  /** Rooms that reached `bucket_size`. */
  full: number;
}

/**
 * WHAT a reward's rank counts. "entity" is the board's own ranked entity — a
 * player on a player board, a GUILD on scope=guild_aggregated — and is every
 * reward there has ever been, which is why the server substitutes it when the
 * field is absent.
 *
 * "guild_member" is the second table a guild_score_source="member_contribution"
 * board grows: the member's place by contribution INSIDE their own team, in
 * every team that entered. It is accepted on that source alone, only as a rank
 * range (rank_from/rank_to required), and never with a tier_key or a
 * percentile.
 *
 * Fixed at create like the range it goes with: RewardUpdateParams carries no
 * field for it, so UpdateRewardInput below has none either.
 */
export type RankBasis = "entity" | "guild_member";

/**
 * Rank-, percentile- or tier-threshold-based prize definition.
 * Mirrors leaderboard.Reward.
 */
export interface Reward {
  id: string;
  leaderboard_id: string;
  /**
   * Always present — the Go field carries no `omitempty` and the column is NOT
   * NULL DEFAULT 'entity', so every reward answers with one of the two values
   * whether or not it was created after this feature.
   */
  rank_basis: RankBasis;
  tier_key?: string;
  rank_from?: number;
  rank_to?: number;
  percentile_from?: number;
  percentile_to?: number;
  name: string;
  /**
   * Free-form JSON — an object or an array of items. The column is JSONB and
   * no backend path reads its keys, so the shape is the game's business.
   */
  payload: unknown;
  position: number;
  created_at: string;
}

/**
 * Mirrors leaderboard.Reward.IsTierThreshold(): the reward is a claim_gated
 * ladder rung. There is no reward-type column anywhere — migration 0044's
 * third lb_rewards_range_xor branch (no rank range, no percentile range,
 * tier_key NOT NULL) is the whole definition, so the type is derived, never
 * stored.
 */
export function isTierThresholdReward(
  r: Pick<Reward, "rank_from" | "percentile_from" | "tier_key">,
): boolean {
  const noRank = r.rank_from === undefined || r.rank_from === null;
  const noPct = r.percentile_from === undefined || r.percentile_from === null;
  return noRank && noPct && !!r.tier_key;
}

/**
 * POST /rewards body. Mirrors leaderboard.RewardCreateParams. The DB CHECK
 * (lb_rewards_range_xor) enforces XOR between (rank_from, rank_to) and
 * (percentile_from, percentile_to) — or, since migration 0044, neither range
 * at all plus a tier_key, which is what makes the reward a tier threshold.
 */
export interface CreateRewardInput {
  /** Omitted defaults to "entity" — the server substitutes it, so the ordinary
     reward's body is byte-for-byte what it was before this field existed. */
  rank_basis?: RankBasis;
  tier_key?: string;
  rank_from?: number;
  rank_to?: number;
  percentile_from?: number;
  percentile_to?: number;
  name: string;
  payload?: unknown;
  position: number;
}

/** PATCH /rewards/{rid} body. Mirrors leaderboard.RewardUpdateParams. */
export interface UpdateRewardInput {
  name?: string;
  payload?: unknown;
  position?: number;
}

/** Materialized reward for a player after a cycle closes. Mirrors leaderboard.RewardGrant. */
export interface RewardGrant {
  id: string;
  cycle_id: string;
  leaderboard_id: string;
  reward_id: string;
  player_id: string;
  guild_id?: string;
  final_rank: number;
  final_score: number;
  tier_key?: string;
  status: GrantStatus;
  claimed_at?: string;
  expires_at: string;
  payload: unknown;
  name: string;
  created_at: string;
}

/** One row of the submit audit log. Mirrors leaderboard.AuditEntry. */
export interface AuditEntry {
  id: number;
  cycle_id: string;
  entity_id: string;
  channel: AuditChannel;
  submitted_score: number;
  applied_score?: number;
  /**
   * What the entity had stored BEFORE this event — `submitted_score` is what
   * arrived, `applied_score` is what it became. Absent on the two
   * pre-transactional guards (`guard_max_score`, `guard_interval`), which fire
   * before the score row is read, and on every row written before migration
   * 0058 — the audit keeps 7 days, so those age out on their own.
   */
  current_score?: number;
  reject_reason?: string;
  request_id: string;
  /** Set when channel='admin' (Phase 5.3c). */
  admin_user_id?: string;
  created_at: string;
  /**
   * Which of the two the entity_id turned out to be. Only present when the
   * read asked for ?expand=entity AND the id resolved — a guild_aggregated
   * board logs client submits against the player and admin moderation against
   * the guild, so this is what says which card the name links to.
   */
  entity_kind?: EntityKind;
  /** Resolved display name (player display_name or guild name). */
  entity_name?: string;
  /** Email of the admin behind a channel='admin' row. */
  actor_email?: string;
}

/** Audit query filter — translated into ?entity, ?reject_reason, ?cursor, ?limit, ?expand. */
export interface AuditListFilter {
  entity_id?: string;
  reject_reason?: string;
  cursor?: string;
  limit?: number;
  /** ?expand keys; "entity" fills entity_kind/entity_name/actor_email. */
  expand?: string[];
}

/** PATCH /cycles/{cycle_id}/scores/{entity_id} body. */
export interface ScoreAdjustInput {
  score: number;
}

// ---------------------------------------------------------------------------
// Rewards file (t-43) — mirrors internal/admin/snapshot/rewards.go.
//
// Deliberately UUID-free, like the project snapshot it borrows its envelope
// from: a reward is recognised by its range, never by an id from whichever
// environment produced the file.
// ---------------------------------------------------------------------------

/**
 * One reward as it travels in the file. Every field of `Reward` except `id`,
 * `leaderboard_id` and `created_at`. Exactly one of the three range shapes is
 * populated (rank, percentile, or a tier threshold with no range at all).
 *
 * Nulls are spelled out because the file is hand-editable: Go's omitempty
 * never writes them, but an operator who clears a field by hand does.
 */
export interface RewardsFileEntry {
  /**
   * Absent means "entity", exactly as it does in a create body. A reward is
   * recognised by the PAIR (rank_basis + range), so the same `1 – 1` in the
   * team table and in the contributor table are two different rewards and
   * neither import mode may fold them into one.
   */
  rank_basis?: RankBasis;
  tier_key?: string | null;
  rank_from?: number | null;
  rank_to?: number | null;
  percentile_from?: number | null;
  percentile_to?: number | null;
  name: string;
  /** Free-form JSON — the game's business, never read by the platform. */
  payload?: unknown;
  position: number;
}

/**
 * Where the file came from. Purely informational — the import screen shows it
 * so a human can tell at a glance that they picked the wrong export; nothing
 * keys off it, and a file from another project imports just as well.
 */
export interface RewardsFileSource {
  host: string;
  project_name: string;
  board_ext_key: string;
  board_name: string;
}

/** The exported file itself. `schema` is exact-matched on import. */
export interface RewardsFile {
  schema: string;
  exported_at: string;
  source: RewardsFileSource;
  rewards: RewardsFileEntry[];
}

/**
 * What the file does to the rewards already on the board: "merge" touches only
 * the ranges the file names, "replace" makes the file the whole table.
 */
export type RewardsImportMode = "merge" | "replace";

/** One reward the file rewrites in place, both sides for the diff row. */
export interface RewardsUpdate {
  before: RewardsFileEntry;
  after: RewardsFileEntry;
}

/**
 * The server's reading of the file against the board, computed without
 * writing anything. `kept` is the merge-mode counterpart of `delete`: rewards
 * the file says nothing about, which stay exactly as they are.
 */
export interface RewardsPlan {
  mode: RewardsImportMode;
  create: RewardsFileEntry[];
  update: RewardsUpdate[];
  delete: RewardsFileEntry[];
  unchanged: RewardsFileEntry[];
  kept: RewardsFileEntry[];
}

/**
 * What the import actually did — counted by the server after the transaction
 * committed, not predicted by the plan, plus the resulting table so the caller
 * can show it without a re-fetch.
 */
export interface RewardsImportResult {
  created: number;
  updated: number;
  deleted: number;
  unchanged: number;
  rewards: Reward[];
}

/** Wrappers for handler response envelopes (`{leaderboards: [...]}`, etc.). */
export interface ListLeaderboardsResponse {
  leaderboards: Leaderboard[];
}

export interface ListCyclesResponse {
  cycles: Cycle[];
  cursor: string;
}

export interface ListBucketsResponse {
  buckets: Bucket[];
}

export interface ListRewardsResponse {
  rewards: Reward[];
}

export interface ListAuditResponse {
  audit: AuditEntry[];
  cursor: string;
}

// ---------------------------------------------------------------------------
// Scheduled (pending) changes — t-159
//
// A board's configuration is answered two ways at once: the open cycle reads
// the snapshot it was cut with, the next one reads the board. That is what
// lets a change wait — a package is a set of column values held back until the
// next cut, at which point it is re-validated and either applied whole or
// refused whole.
// ---------------------------------------------------------------------------

export type PendingStatus = "pending" | "applied" | "failed";

/**
 * What becomes of the leagues players already hold when a package takes the
 * board off a ladder. Not configuration — no cycle snapshot carries it — which
 * is why the package has to say it out loud.
 */
export type LadderDecision = "keep" | "reset";

/**
 * One row of the prize table as it travels in a package: the same fields as a
 * cycle's `rewards_snapshot` row, without the id and the timestamps a stored
 * reward carries.
 */
export interface RewardSpec {
  tier_key?: string;
  rank_from?: number;
  rank_to?: number;
  percentile_from?: number;
  percentile_to?: number;
  rank_basis?: RankBasis;
  name: string;
  payload: unknown;
  position: number;
}

export interface PendingPackage {
  id: string;
  leaderboard_id: string;
  status: PendingStatus;
  /** Column name → the value the next cut will write. Untyped on purpose: the
     set is open, and the diff reads it against the board rather than against a
     schema. */
  fields: Record<string, unknown>;
  /** The whole prize table, or absent when the package does not touch it. An
     empty array is a real instruction — clear the table — and is not the same
     as leaving the key out. */
  rewards?: RewardSpec[];
  ladder_decision?: LadderDecision;
  actor_email: string;
  failure_reason?: string;
  failure_field?: string;
  created_at: string;
  updated_at: string;
  resolved_at?: string;
}

/**
 * How many players hold a league on this board, and on which rung. Asked only
 * when a package is about to take the board off its ladder: the answer decides
 * whether the operator is choosing about 3 rows or about 30 000.
 */
export interface LadderImpact {
  players: number;
  tiers: { tier_key: string; players: number }[];
}
